import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Gauge, Lock, Unlock, Check } from "lucide-react";

const OPTIONS = {
  experience: [["none", "None — this is new to me"], ["some", "Some — a few years"],
               ["experienced", "Experienced investor"], ["professional", "I work in finance"]],
  horizon: [["under_1y", "Less than a year"], ["1_3y", "1 to 3 years"],
            ["3_10y", "3 to 10 years"], ["over_10y", "More than 10 years"]],
  loss_tolerance: [["none", "I can't afford any loss"], ["small", "A small dip is fine"],
                   ["moderate", "I can ride out a 20% fall"], ["large", "I accept large swings"]],
};

const LABELS = { experience: "Investing experience", horizon: "How long you're investing for",
                 loss_tolerance: "How much loss you can live with" };

/** Profile → Risk profile. Decides which products the client is cleared for. */
export const RiskProfileCard = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ experience: "some", horizon: "3_10y", loss_tolerance: "moderate",
                                     income_usd: 0, liquid_savings_usd: 0, dependents: 0 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/foundation/risk/profile");
    setData(data);
    if (data.assessment) setForm((f) => ({ ...f, ...data.assessment }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/foundation/risk/assess", {
        ...form, income_usd: Number(form.income_usd), liquid_savings_usd: Number(form.liquid_savings_usd),
        dependents: Number(form.dependents) });
      toast.success(`You're a ${res.profile} investor`, { description: `Suitability score ${res.risk_score}/10.` });
      load();
    } catch (e) { toast.error("Couldn't save that", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const unlock = async (product, label) => {
    try {
      await api.post("/foundation/risk/acknowledge", { product });
      toast.warning(`${label} unlocked`, { description: "You've accepted this against our suitability assessment. It's on the record." });
      load();
    } catch (e) { toast.error("Couldn't unlock", { description: e?.response?.data?.detail }); }
  };

  if (!data) return null;

  return (
    <Card className="p-5 shadow-premium" data-testid="risk-profile-card">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Gauge className="h-4 w-4 text-[color:var(--tharwy-lime)]" />Risk profile &amp; suitability
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
        {data.risk_score
          ? `You're assessed as ${data.profile} — ${data.risk_score}/10. This decides which products you can use.`
          : "Answer three questions and we'll work out which products suit you. Until then only savings and portfolios are open."}
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {Object.keys(OPTIONS).map((k) => (
          <div key={k}>
            <Label className="text-xs">{LABELS[k]}</Label>
            <Select value={form[k]} onValueChange={(v) => setForm((f) => ({ ...f, [k]: v }))}>
              <SelectTrigger className="mt-1.5" data-testid={`risk-${k}-select`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {OPTIONS[k].map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div>
          <Label className="text-xs">Annual income (USD)</Label>
          <Input type="number" value={form.income_usd} data-testid="risk-income-input"
            onChange={(e) => setForm((f) => ({ ...f, income_usd: e.target.value }))} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs">Savings you could lose (USD)</Label>
          <Input type="number" value={form.liquid_savings_usd} data-testid="risk-savings-input"
            onChange={(e) => setForm((f) => ({ ...f, liquid_savings_usd: e.target.value }))} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs">People who depend on you</Label>
          <Input type="number" value={form.dependents} data-testid="risk-dependents-input"
            onChange={(e) => setForm((f) => ({ ...f, dependents: e.target.value }))} className="mt-1.5" />
        </div>
      </div>

      <Button className="mt-4" onClick={submit} disabled={busy} data-testid="risk-submit-btn">
        {busy ? "Working it out..." : data.risk_score ? "Update my assessment" : "Assess me"}
      </Button>

      <div className="mt-5 space-y-2" data-testid="risk-eligibility">
        {Object.entries(data.eligibility).map(([product, v]) => (
          <div key={product} className="flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5"
            data-testid={`risk-product-${product}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                {v.allowed ? <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                           : <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                {v.label}
              </div>
              <div className="text-xs text-muted-foreground leading-relaxed mt-0.5">{v.reason}</div>
            </div>
            {v.override_allowed && (
              <Button variant="ghost" size="sm" className="shrink-0" onClick={() => unlock(product, v.label)}
                data-testid={`risk-unlock-${product}`}>
                <Unlock className="h-3.5 w-3.5 me-1.5" />Unlock anyway
              </Button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};
